"use client";

import Link from "next/link";
import { ChevronRight } from "lucide-react";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import { TeamLogo } from "@/components/ui/TeamLogo";
import type { TierTeam } from "../../data/getNationsLeagueData";

interface TeamsSectionProps {
    teams: TierTeam[];
}

/**
 * Tier teams grid. Groups teams under their group heading and links each to
 * `nav.team(id)` — same routing target the rest of the site uses.
 */
export function TeamsSection({ teams }: TeamsSectionProps) {
    const nav = useNavigation();
    const groups = groupBy(teams, (t) => t.groupName ?? "");

    return (
        <div className="max-w-350 mx-auto px-4 py-6 space-y-8">
            {Object.entries(groups).map(([groupName, list]) => (
                <section key={groupName}>
                    {groupName && (
                        <h2 className="text-sm font-black uppercase tracking-widest mb-3">{groupName}</h2>
                    )}
                    <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {list.map((t) => (
                            <li key={t.id}>
                                <Link
                                    href={nav.team(t.id)}
                                    prefetch={false}
                                    className="group flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-3 hover:shadow-md transition-shadow"
                                >
                                    <TeamLogo
                                        src={t.logo}
                                        alt={t.name}
                                        size="md"
                                        countryFlag={t.countryFlag}
                                        countryCode={t.countryCode}
                                    />
                                    <span className="min-w-0 flex-1">
                                        <span className="block text-sm font-semibold truncate">{t.name}</span>
                                        {t.shortName && (
                                            <span className="block text-[10px] uppercase tracking-wider text-muted-foreground">
                                                {t.shortName}
                                            </span>
                                        )}
                                    </span>
                                    <ChevronRight className="w-4 h-4 shrink-0 text-muted-foreground group-hover:text-foreground transition-colors" />
                                </Link>
                            </li>
                        ))}
                    </ul>
                </section>
            ))}
        </div>
    );
}

function groupBy<T>(items: T[], key: (t: T) => string): Record<string, T[]> {
    const out: Record<string, T[]> = {};
    for (const item of items) {
        const k = key(item);
        (out[k] ??= []).push(item);
    }
    return out;
}
